import { Component, inject, Input, OnInit } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable, of, shareReplay } from 'rxjs';
import { FileDownloadingHelperService, PdfService, RegularDateFormat } from '@anecdotes/helpers';
import { EvidenceFileExtension } from '@anecdotes/data-store';
import {
  DownloadingResource,
  TrustCenterEventService,
  TrustCenterFacadeService,
  EvidenceFile,
  DownloadEvidenceParams,
} from '@anecdotes/shared-trust-center';
import { DatePipe } from '@angular/common';
import { switchMap } from 'rxjs/operators';
import { WindowHelperService } from '@anecdotes/utils';

@Component({
  template: '',
  standalone: true,
})
export class BaseEvidenceDownloadComponent implements OnInit {
  protected readonly fileDownloadingHelper = inject(FileDownloadingHelperService);
  protected readonly trustCenterFacadeService = inject(TrustCenterFacadeService);
  protected readonly pdfService = inject(PdfService);
  protected readonly datePipe = inject(DatePipe);
  protected readonly trustCenterEventService = inject(TrustCenterEventService);
  protected readonly windowHelper = inject(WindowHelperService);

  readonly loading$ = new BehaviorSubject<boolean>(false);

  hasFilesWatermark: boolean;

  hasPendingAccess$: Observable<boolean>;

  isPreviewMode$: Observable<boolean>;

  isLoggedIn$: Observable<boolean>;

  @Input({ required: true })
  downloadingResource: DownloadingResource;

  @Input({ required: true })
  resourceId: string;

  async ngOnInit(): Promise<void> {
    this.hasPendingAccess$ = this.trustCenterFacadeService.hasPendingAccess()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.isPreviewMode$ = this.trustCenterFacadeService.isPreviewMode()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.isLoggedIn$ = this.trustCenterFacadeService.isLoggedIn();

    this.hasFilesWatermark = await firstValueFrom(this.trustCenterFacadeService.hasFilesWatermark());
  }

  async downloadEvidence(evidence: DownloadEvidenceParams): Promise<void> {
    const isPreviewMode = await firstValueFrom(this.isPreviewMode$);

    if (this.loading$.value || isPreviewMode) {
      return;
    }

    try {
      this.loading$.next(true);
      const fileName = this.getFileName(evidence);
      const companyName = await firstValueFrom(this.trustCenterFacadeService.getName());

      this.trustCenterEventService.trackDownloadFileClick(
        this.downloadingResource,
        evidence.evidence_id,
        evidence.evidenceInstanceId,
        fileName,
        companyName,
      );

      const file = await this.trustCenterFacadeService.downloadEvidence(evidence);
      const fileType = file.name.split('.').reverse()[0];

      if (!evidence.is_public && this.hasFilesWatermark && fileType.toLowerCase() === EvidenceFileExtension.PDF.toLowerCase()) {
        await this.downloadPdfWithWatermark(file, fileName);
      } else {
        this.fileDownloadingHelper.downloadFile(file, fileName);
      }
    } finally {
      this.loading$.next(false);
    }
  }

  async openLinkEvidence(evidenceUrl: string): Promise<void> {
    const isPreviewMode = await firstValueFrom(this.isPreviewMode$);

    if (isPreviewMode) {
      return;
    }

    const resultLink = !evidenceUrl.startsWith('http') ? `https://${evidenceUrl}` : evidenceUrl;
    this.windowHelper.openUrlInNewTab(resultLink);
  }

  canDownloadEvidence(evidence: EvidenceFile): Observable<boolean> {
    return this.isPreviewMode$
      .pipe(
        switchMap((isPreviewMode: boolean) => {
          const canDownload$ = this.trustCenterFacadeService.hasPermissionToResource(this.downloadingResource, this.resourceId)
            .pipe(map((hasPermission) => hasPermission || evidence.is_public));

          return isPreviewMode ? of(evidence.is_public) : canDownload$;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
  }

  private getFileName(evidence: EvidenceFile): `${string}.${string}` {
    const fileName = evidence.trustcenter_display_name;
    const fileType = evidence.filename.split('.').reverse()[0];
    const extension = `.${fileType}`;

    if (fileName.endsWith(extension)) {
      return `${fileName.slice(0, -extension.length)}.${fileType}`;
    }

    return `${fileName}.${fileType}`;
  }

  private async downloadPdfWithWatermark(file: File, fileName: string): Promise<void> {
    const userEmail = await firstValueFrom(this.trustCenterFacadeService.getUserEmail());
    const currentDate = this.datePipe.transform(new Date(), RegularDateFormat);

    this.pdfService.downloadPdfWithWatermark(
      file,
      [userEmail, currentDate],
      undefined,
      { customFileName: fileName },
    );
  }
}
